import { useRef, useEffect, useState, type PropsWithChildren } from 'react';
import { Text, type TextT } from '@mentimeter/ragnar-ui/text';
import { Tooltip } from '@mentimeter/ragnar-ui/tooltip';

const useIsTruncated = () => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  return { ref, isTruncated };
};

const ConditionalTooltipWrapper = ({
  tooltipText,
  children,
}: PropsWithChildren<{ tooltipText: string | undefined }>) => {
  if (tooltipText) {
    return (
      <Tooltip trigger={children} side="bottom" withPortal={false}>
        <Text fontSize="125" color="inherit">
          {tooltipText}
        </Text>
      </Tooltip>
    );
  }

  return children;
};

interface OptionLabelProps extends TextT {
  text: string;
  fontSize: number;
}

export const OptionLabel = ({ text, fontSize, ...rest }: OptionLabelProps) => {
  const { ref, isTruncated } = useIsTruncated();

  const tooltipText = isTruncated ? text : undefined;
  return (
    <ConditionalTooltipWrapper tooltipText={tooltipText}>
      <Text
        ref={ref}
        color="textWeak"
        fontSize={fontSize}
        lineHeight="none"
        fontWeight="regular"
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%',
          display: 'block',
        }}
        {...rest}
      >
        {text}
      </Text>
    </ConditionalTooltipWrapper>
  );
};
